/**
 * Theme and density preferences.
 *
 * Both axes are attributes on <html> so the token layer in tokens.css can remap
 * against them with no component involvement. index.html stamps the stored
 * values before first paint; this module is the single writer afterwards.
 */

export type Theme = 'canon' | 'terminal';
export type Density = 'compact' | 'normal' | 'relaxed';

export const THEMES: Theme[] = ['canon', 'terminal'];
export const DENSITIES: Density[] = ['compact', 'normal', 'relaxed'];

const THEME_KEY = 'wft-theme';
const DENSITY_KEY = 'wft-density';

/**
 * The routes are prerendered in Node, where there is no document and no
 * storage. Every accessor below is guarded so the build renders the documented
 * defaults instead of throwing, and the real values are picked up on mount.
 */
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function isTheme(v: unknown): v is Theme {
  return v === 'canon' || v === 'terminal';
}

function isDensity(v: unknown): v is Density {
  return v === 'compact' || v === 'normal' || v === 'relaxed';
}

/** Storage is unavailable in private mode and in some embedded webviews. */
function safeGet(key: string): string | null {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* preference simply does not persist */
  }
}

/**
 * Canon (light) is what a first-time visitor sees. Terminal (dark) is one
 * toggle away and is remembered in localStorage, so the choice follows the
 * visitor across every route and every later visit rather than resetting per
 * page.
 */
export const DEFAULT_THEME: Theme = 'canon';

export function readTheme(): Theme {
  const stored = safeGet(THEME_KEY);
  if (isTheme(stored)) return stored;
  if (!isBrowser) return DEFAULT_THEME;
  const attr = document.documentElement.getAttribute('data-wft-theme');
  return isTheme(attr) ? attr : DEFAULT_THEME;
}

export function readDensity(): Density {
  const stored = safeGet(DENSITY_KEY);
  if (isDensity(stored)) return stored;
  if (!isBrowser) return 'normal';
  const attr = document.documentElement.getAttribute('data-wft-density');
  return isDensity(attr) ? attr : 'normal';
}

export function applyTheme(theme: Theme): void {
  if (!isBrowser) return;
  document.documentElement.setAttribute('data-wft-theme', theme);
  safeSet(THEME_KEY, theme);
}

export function applyDensity(density: Density): void {
  if (!isBrowser) return;
  document.documentElement.setAttribute('data-wft-density', density);
  safeSet(DENSITY_KEY, density);
}

export function nextDensity(current: Density): Density {
  return DENSITIES[(DENSITIES.indexOf(current) + 1) % DENSITIES.length];
}

/** Demo sign-in flag. Browser-only: there is no auth endpoint. */
const SIGNED_IN_KEY = 'wft-demo-signed-in';

export function isSignedIn(): boolean {
  if (!isBrowser) return false;
  try {
    return sessionStorage.getItem(SIGNED_IN_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSignedIn(value: boolean): void {
  if (!isBrowser) return;
  try {
    if (value) sessionStorage.setItem(SIGNED_IN_KEY, '1');
    else sessionStorage.removeItem(SIGNED_IN_KEY);
  } catch {
    /* private mode — the demo gate simply does not persist */
  }
}
